import { useRef } from 'react'
import { animated, config, useSprings } from '@react-spring/web'
import { createUseGesture, dragAction } from '@use-gesture/react'
import clamp from 'lodash/clamp'
import swap from 'lodash-move'

import styles from './DraggableList.module.scss'

const height = 50
const gap = 10
const fn =
  (order: number[], active = false, originalIndex = 0, curIndex = 0, y = 0) =>
    (index: number) =>
      active && index === originalIndex
        ? {
            y: curIndex * (height + gap) + y,
            scale: 1.1,
            zIndex: 1,
            shadow: 15,
            immediate: (key: string) => key === 'zIndex',
            config: (key: string) => (key === 'y' ? config.stiff : config.default)
          }
        : {
            y: order.indexOf(index) * (height + gap),
            scale: 1,
            zIndex: 0,
            shadow: 1,
            immediate: false
          }

export function DraggableList ({ items, dragCallback }: { items: string[], dragCallback?: (order: number[]) => void }) {
  const order = useRef(items.map((_, index) => index)) // Store indices as a local ref, this represents the item order
  const [springs, api] = useSprings(items.length, fn(order.current)) // Create springs, each corresponds to an item, controlling its transform, scale, etc.

  const useGesture = createUseGesture([dragAction])
  const bind = useGesture({
    onDrag: ({ args: [originalIndex], active, movement: [, y] }) => {
      const curIndex = order.current.indexOf(originalIndex)
      const curRow = clamp(Math.round((curIndex * (height + gap) + y) / (height + gap)), 0, items.length - 1)
      const newOrder = swap(order.current, curIndex, curRow)
      api.start(fn(newOrder, active, originalIndex, curIndex, y)) // Feed springs new style data, they'll animate the view without causing a single render
      if (!active) order.current = newOrder
    },
    onDragEnd: () => {
      if (dragCallback) dragCallback(order.current)
    }
  })

  return (
    <div className={styles.content} style={{ height: items.length * (height + gap) }}>
      {springs.map(({ zIndex, shadow, y, scale }, i) => (
        <animated.div
          {...bind(i)}
          key={i}
          style={{
            zIndex,
            boxShadow: shadow.to(s => `rgba(0, 0, 0, 0.15) 0px ${s}px ${2 * s}px 0px`),
            y,
            scale
          }}
        >
          {items[i]}
        </animated.div>
      ))}
    </div>
  )
}
